/**
 * The pinned projects, as a section of the sidebar.
 *
 * ── WHY THE RAIL AS WELL AS THE STRIP ──────────────────────────────────────
 *
 * The strip over the register (`sections/projects/PinnedProjects.tsx`) answers
 * the question an operator asks WHILE they are looking at projects. This answers
 * the one they ask from everywhere else: they are in a ticket, on a call, in the
 * mail, and the project they have been three days deep in is two clicks and a
 * scan away - open the register, find the row. The sidebar is the one thing on
 * screen whichever page they are on, so a shortcut that lives there is a
 * shortcut that is always where they left it.
 *
 * Both are drawn, on purpose. They are not two copies of one control: the strip
 * counts what the archive is holding, it has no ceiling, and it belongs to the
 * register; this is a list of destinations, like every other row in the rail.
 *
 * Both can unpin, and that is the point rather than an overlap. The rail is
 * where a stale pin is NOTICED - it is on screen all day, on every page - so
 * sending the operator to the register to undo one click made the shortcut a
 * one-way door. See `.crm-pin-off` in `index.css` for why the control hides
 * until the pointer is on the row.
 *
 * ── WHY IT IS NOT A NAV GROUP ──────────────────────────────────────────────
 *
 * `CRM_NAV` is a static registry of destinations gated by permission, and every
 * item in it is a page that exists whether or not anybody has ever been there.
 * These rows are DATA - per-operator, fetched, and gone the moment a pin is
 * removed. Putting them in the registry would mean the rail's own list changing
 * shape at runtime, and `nav.ts` staying honest about what a nav item is matters
 * more than the few lines it would save here.
 *
 * ── WHY IT IS CAPPED ───────────────────────────────────────────────────────
 *
 * The section sits at the TOP of the rail, because a shortcut five sections down
 * a scrolling column is not a shortcut. That is also what makes its length
 * somebody else's problem: the server lets one operator hold fifty pins, and
 * fifty rows here would push Dashboard, Mail and Settings clean off the screen.
 *
 * So the rail draws the first few and says how many it left out, with the
 * overflow row going to the register - where the strip draws every one of them
 * with no ceiling at all. A cap that keeps quiet would be the worse half of both
 * options: an operator who pinned twelve would count eight and conclude the pin
 * had failed.
 *
 * ── THE COLLAPSED RAIL ─────────────────────────────────────────────────────
 *
 * A 44px icon pill has room for one glyph, and the same pin glyph six times over
 * is six identical buttons. So the collapsed row carries the project's INITIAL
 * instead, with the name in the rail's own tooltip on hover - which is why this
 * component is handed `onTip`/`onTipOut` rather than growing a tooltip of its
 * own: one hovering layer owned by the rail, or two of them fighting over the
 * same corner of the screen.
 *
 * ── THE PILL IS THE PROGRESS BAR ───────────────────────────────────────────
 *
 * Not a hairline under the label, and not a percentage printed beside it: the
 * row's own background is filled to the project's percentage, and the part that
 * is not done stays as a faint track in the same hue. The rail is 190px wide
 * with a name already truncating in it, so a bar and a figure would both be
 * taken out of the name - and the background was empty anyway.
 *
 * The figure comes from `effectiveProgressPct`, which the pins endpoint resolves
 * the same way the register does (stored number on a manual project, task rollup
 * on a "tasks" one). Falling back to the stored `progressPct` when it is absent
 * is what `projects.ts` asks every screen to do: a stale number beats a blank
 * bar, which reads as a failed load.
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { NavLink } from "react-router-dom";
import { PinOff } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { userCanPlatformAny, type StaffUser } from "../../lib/auth";
import { getPinnedProjects, onPinsChanged, unpinProject, type PinnedProject } from "../../lib/projectPins";
import { useToast } from "../ui";

/** The same key that gates `/projects` in `nav.ts` and the route behind it. A
 *  shortcut must never reach a page its owner may not open. */
const PROJECTS_PERM = "manage:crm.projects";

/** How many rows the rail draws before it starts counting instead. Eight is
 *  about a screen's worth above the first nav section on a laptop; the rest are
 *  one click away on the register, which has no ceiling. */
const RAIL_PINS = 8;

/** The glyph for the collapsed rail: one upper-case letter, from the first
 *  character that is not a space. `Array.from` rather than `[0]`, so a name that
 *  opens on an emoji or any other surrogate pair yields the whole character
 *  instead of half of one. */
function initial(name: string): string {
  return (Array.from(name.trim())[0] ?? "?").toUpperCase();
}

/**
 * The percentage the row is filled to, as a whole number between 0 and 100.
 *
 * Clamped rather than trusted. The value ends up in a CSS `width`, and a project
 * whose manual figure somebody typed as 120 would paint a fill wider than the
 * pill it is inside - the overflow is hidden, so the bug would not look like a
 * bug, it would look like a project at 100% that never moves.
 */
function fillPct(project: PinnedProject): number {
  const raw = project.effectiveProgressPct ?? project.progressPct ?? 0;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

export function RailPins({
  user,
  collapsed,
  onTip,
  onTipOut,
  onNavigate,
}: {
  user: StaffUser;
  /** Read only to decide which of the two hover hints is offered - see the row
   *  below. The collapsed LAYOUT is CSS on `html.crm-mini`, like the rest of the
   *  rail, so nothing here has to redraw when the rail is folded. */
  collapsed: boolean;
  /** The rail's own tooltip, borrowed for the collapsed state. It stays silent
   *  while the rail is expanded, which is why a native hint is offered there. */
  onTip: (e: React.MouseEvent<HTMLElement>, label: string) => void;
  onTipOut: () => void;
  /** The drawer's close signal, same as every nav row. */
  onNavigate?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const allowed = userCanPlatformAny(user, PROJECTS_PERM);
  const [rows, setRows] = useState<PinnedProject[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      const res = await getPinnedProjects();
      setRows(res.rows);
    } catch {
      // Silent, and it leaves whatever was on screen. This is a shortcut inside
      // the furniture: an error state in the sidebar would be a convenience
      // shouting about itself on every page in the product.
    }
  }, [allowed]);

  useEffect(() => {
    void load();
  }, [load]);

  // A pin set on the project page or in the register's row menu is a pin this
  // list has to follow, and it is nowhere near either of them - see
  // `PINS_CHANGED` for why that arrives as an event rather than as a prop.
  //
  // The payload is ignored on purpose. This list is capped, so a pin removed
  // here can promote a ninth one that was never on screen: only the server
  // knows what the shortlist is now.
  useEffect(() => onPinsChanged(() => void load()), [load]);

  // Nothing pinned: draw NOTHING, not an empty heading. The rail is the one
  // element on every screen, and a permanent section explaining a feature most
  // operators have not used is furniture that never earns its height.
  if (!allowed || rows.length === 0) return null;

  const shown = rows.slice(0, RAIL_PINS);
  const hidden = rows.length - shown.length;

  /**
   * Drop the pin, from wherever the operator happens to be.
   *
   * The row leaves the rail before the request lands. The pin is this
   * operator's own bookmark, the write cannot be refused for anything but a
   * network failure, and a row that sits still after the click reads as a
   * button that did nothing - in the sidebar, where the same eight rows are on
   * screen all day, that reading is expensive.
   *
   * Nothing here re-counts the overflow row. `unpinProject` announces
   * `PINS_CHANGED`, this component is already listening, and the reload that
   * follows is what promotes the ninth pin into the list and drops "+1 more".
   * Doing the arithmetic locally as well would be a second opinion about a
   * number the server is about to send.
   */
  async function unpin(id: string) {
    if (busy) return;
    setBusy(id);
    setRows((prev) => prev.filter((p) => p.id !== id));
    try {
      await unpinProject(id);
    } catch {
      // The one place this section speaks. `load` above is deliberately silent
      // about a failed FETCH, because nobody asked for it; this failure is the
      // receipt for a click, and a click that quietly did nothing is worse in
      // the furniture than anywhere else - the operator is on another page and
      // will not go looking for the reason.
      toast(t("common.error"));
      await load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="crm-sect">{t("projects.pinnedTitle")}</div>
      {shown.map((p) => {
        // The client rides along in the hint rather than in the row: two projects
        // called "Website" are ordinary, and the rail is too narrow to tell them
        // apart on a second line.
        const pct = fillPct(p);
        const hint = `${p.client ? `${p.name} - ${p.client.name}` : p.name} - ${pct}%`;
        return (
          // The link and the unpin button are SIBLINGS inside the row, not
          // nested: a button inside an `<a>` is invalid HTML and leaves a screen
          // reader with one target holding two actions. Same shape `Rail.tsx`
          // uses for a nav item that carries a chevron.
          <div key={p.id} className="crm-pin-row">
            <NavLink
              to={`/projects/${p.id}`}
              className={({ isActive }) => cn("crm-nav crm-nav-pin min-w-0 flex-1", isActive && "is-on")}
              // One hint at a time. Collapsed, the rail's own tooltip carries it;
              // expanded, that tooltip stays silent and the browser's does the job.
              // Both at once is the same sentence twice in two different boxes.
              title={collapsed ? undefined : hint}
              onMouseEnter={(e) => onTip(e, hint)}
              onMouseLeave={onTipOut}
              onClick={() => {
                onTipOut();
                onNavigate?.();
              }}
            >
              {/* The bar itself, behind the label. A span rather than a pseudo-
                  element on the row, because it needs two layers of its own (the
                  filled part and the track behind it) and the row's `::after` is
                  already spoken for by the collapsed rail's badge dot.

                  `aria-hidden`, and the number goes into the hint above instead:
                  a screen reader reading a decorative div would announce the
                  project twice, and a bar with no figure anywhere is the thing
                  nobody can interpret. */}
              <span
                className="crm-pin-fill"
                aria-hidden="true"
                style={{ "--crm-pin-pct": `${pct}%` } as CSSProperties}
              />
              <span className="crm-nav-ini" aria-hidden="true">
                {initial(p.name)}
              </span>
              <span className="crm-nav-l">{p.name}</span>
            </NavLink>

            {/* The project's name rides in the label, because "Unpin project"
                eight times over is eight identical announcements and no way to
                tell which row is which. The visible `title` stays short - the
                name is already on the row, an inch to the left. */}
            <button
              type="button"
              className="crm-pin-off"
              disabled={busy === p.id}
              title={t("projects.unpin")}
              aria-label={`${t("projects.unpin")}: ${p.name}`}
              onClick={() => void unpin(p.id)}
            >
              <PinOff className="size-3" strokeWidth={2} />
            </button>
          </div>
        );
      })}

      {/* Only when it is true. The register's own strip is where the rest are,
          so this row is a destination and not an apology. */}
      {hidden > 0 ? (
        <NavLink
          to="/projects"
          className="crm-nav crm-nav-pin crm-nav-pin-more"
          title={collapsed ? undefined : t("projects.pinnedMore", { n: hidden })}
          onMouseEnter={(e) => onTip(e, t("projects.pinnedMore", { n: hidden }))}
          onMouseLeave={onTipOut}
          onClick={() => {
            onTipOut();
            onNavigate?.();
          }}
        >
          <span className="crm-nav-ini" aria-hidden="true">
            +{hidden}
          </span>
          <span className="crm-nav-l">{t("projects.pinnedMore", { n: hidden })}</span>
        </NavLink>
      ) : null}
    </>
  );
}
